import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Lock, Users, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { familyCreate, familyJoin, familyInfo, fetchPurchases } from "@/lib/api";
import { sfx } from "@/lib/sounds";
import PaywallModal from "@/components/PaywallModal";

export default function FamilySharing() {
  const [products, setProducts] = useState(null);
  const [info, setInfo] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    fetchPurchases()
      .then((p) => setProducts(p.products))
      .catch(() => setProducts([]));
    familyInfo()
      .then(setInfo)
      .catch(() => {});
  }, []);

  const owned = !!products && (products.includes("family") || products.includes("bundle"));

  const create = async () => {
    setBusy(true);
    sfx.boop();
    try {
      const res = await familyCreate();
      setInfo({ code: res.code });
      sfx.sparkle();
      toast.success("Family code created! Share it with your crew");
    } catch (e) {
      toast.error("Hmm, the cauldron hiccuped — try again");
    }
    setBusy(false);
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    sfx.boop();
    try {
      const res = await familyJoin(joinCode);
      toast.success(`Joined family ${res.code}! Their pages & gallery now show up here`);
      setInfo({ code: res.code });
      setJoinCode("");
      sfx.sparkle();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That code didn't work");
    }
    setBusy(false);
  };

  const copyCode = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(info.code);
    sfx.click();
    toast.success("Code copied!");
  };

  return (
    <div className="mb-16" data-testid="family-section">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-3xl text-lavender sm:text-4xl" style={{ textShadow: "0 0 24px rgba(138,79,255,0.55)" }}>
              Family Sharing
            </h3>
            <span className="rounded-full border border-lavender/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-lavender">
              $10 one-time
            </span>
          </div>
          <p className="mt-1 text-sm text-paper/60">
            Link up to 6 family devices — everyone sees the family's My Pages,
            Haunted Gallery and sticker rewards.
          </p>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        className="card-edge px-6 py-8 sm:px-10"
      >
        {products === null ? (
          <div className="h-24 animate-pulse rounded-2xl bg-white/5" />
        ) : !owned ? (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="family-locked">
            <span className="animate-floaty rounded-full bg-lavender p-4 text-paper shadow-glow-candy">
              <Lock size={26} />
            </span>
            <div className="font-display text-3xl text-paper">One payment. Every device in the family.</div>
            <p className="max-w-md text-sm text-paper/60">
              Unlock Family Sharing once, create a 6-letter code, and let
              grandparents, siblings and cousins join from their own devices.
            </p>
            <button
              data-testid="family-unlock-btn"
              onClick={() => {
                sfx.boop();
                setPaywall(true);
              }}
              className="rounded-full bg-pumpkin px-8 py-3.5 font-bold text-ink shadow-glow-orange transition-transform hover:scale-105 active:scale-95"
            >
              Unlock Family Sharing — $10
            </button>
          </div>
        ) : !info?.code ? (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="family-create">
            <span className="rounded-full bg-lavender/20 p-4 text-lavender">
              <Users size={26} />
            </span>
            <div className="font-display text-3xl text-paper">Create your family code</div>
            <p className="max-w-md text-sm text-paper/60">
              One code per family. Other members type it in on their own device
              to link up — everyone keeps their own art and rewards.
            </p>
            <button
              data-testid="family-create-btn"
              disabled={busy}
              onClick={create}
              className="rounded-full bg-lavender px-8 py-3.5 font-bold text-paper shadow-glow-candy transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
            >
              {busy ? "Brewing..." : "Create Family Code"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 text-center" data-testid="family-panel">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-paper/50">
                Your family code
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                {info.code.split("").map((ch, i) => (
                  <span
                    key={i}
                    className="font-display text-4xl text-slime text-glow-slime sm:text-5xl"
                  >
                    {ch}
                  </span>
                ))}
                <button
                  data-testid="family-copy-btn"
                  onClick={copyCode}
                  aria-label="Copy family code"
                  className="ml-2 rounded-full border border-white/15 p-2.5 text-paper/70 transition-colors hover:border-slime hover:text-slime"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <div className="flex w-full max-w-sm items-center gap-2">
              <input
                data-testid="family-join-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Or join with a code"
                maxLength={6}
                className="min-w-0 flex-1 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 font-display text-xl tracking-[0.3em] text-paper placeholder:font-body placeholder:text-sm placeholder:tracking-normal placeholder:text-paper/40 focus:border-lavender focus:outline-none"
              />
              <button
                data-testid="family-join-btn"
                disabled={busy || !joinCode.trim()}
                onClick={join}
                aria-label="Join family"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-lavender text-paper shadow-glow-candy transition-transform hover:scale-105 disabled:opacity-40"
              >
                <UserPlus size={18} />
              </button>
            </div>

            <p className="max-w-md text-xs leading-relaxed text-paper/45">
              Family members see each other's My Pages and Haunted Gallery
              automatically. Everyone's own stickers and page progress stay
              their own.
            </p>
          </div>
        )}
      </motion.div>

      {paywall && <PaywallModal owned={products || []} onClose={() => setPaywall(false)} />}
    </div>
  );
}
